import { supabase } from '../lib/supabase';
import {
  sendNotaireMissingDocumentEmail,
  sendNotaireDossierRejectedEmail,
  sendNotaireDossierValidatedEmail,
} from './resendEmailService';

// ─── Access Requests ──────────────────────────────────────────────────────────

export const submitAccessRequest = async (requestData) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_access_requests')
      ?.insert([requestData])
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de soumettre la demande. Veuillez réessayer.' };
  }
};

// ─── Notaire Profile ──────────────────────────────────────────────────────────

export const getNotaireProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaries')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.eq('is_verified', true)
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de charger le profil notaire.' };
  }
};

// ─── Dossiers ─────────────────────────────────────────────────────────────────

export const getDossiersByNotaire = async (notaryId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_dossiers')
      ?.select(`
        *,
        property:properties(id, title, address, price),
        buyer:user_profiles!notaire_dossiers_buyer_id_fkey(id, full_name, email, phone),
        seller:user_profiles!notaire_dossiers_seller_id_fkey(id, full_name, email, phone)
      `)
      ?.eq('notary_id', notaryId)
      ?.order('created_at', { ascending: false });
    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: 'Impossible de charger les dossiers.' };
  }
};

export const getDossierById = async (dossierId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_dossiers')
      ?.select(`
        *,
        property:properties(id, title, address, price),
        buyer:user_profiles!notaire_dossiers_buyer_id_fkey(id, full_name, email, phone),
        seller:user_profiles!notaire_dossiers_seller_id_fkey(id, full_name, email, phone)
      `)
      ?.eq('id', dossierId)
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de charger le dossier.' };
  }
};

export const updateDossierStatus = async (dossierId, status, extraFields = {}) => {
  try {
    const updates = { status, ...extraFields };
    if (status === 'instruction_en_cours') updates.accepted_at = new Date()?.toISOString();
    if (status === 'dossier_valide') updates.validated_at = new Date()?.toISOString();
    if (status === 'projet_acte_depose') updates.acte_deposited_at = new Date()?.toISOString();
    if (status === 'transaction_cloturee') updates.closed_at = new Date()?.toISOString();

    const { data, error } = await supabase
      ?.from('notaire_dossiers')
      ?.update(updates)
      ?.eq('id', dossierId)
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de mettre à jour le dossier.' };
  }
};

export const validateDossierConformity = async (dossierId) => {
  try {
    // 1. Validate the dossier and set the badge
    const result = await updateDossierStatus(dossierId, 'dossier_valide', { is_verified_badge: true });
    if (result?.error) return result;

    // 2. Also mark the linked property as notary-verified
    const { data: dossier } = await supabase
      ?.from('notaire_dossiers')
      ?.select(`
        property_id,
        reference,
        notary_id,
        validated_at,
        property:properties(title),
        buyer:user_profiles!notaire_dossiers_buyer_id_fkey(full_name, email),
        seller:user_profiles!notaire_dossiers_seller_id_fkey(full_name, email),
        notary:notaries(name)
      `)
      ?.eq('id', dossierId)
      ?.single();

    if (dossier?.property_id) {
      await supabase
        ?.from('properties')
        ?.update({ is_notary_verified: true })
        ?.eq('id', dossier?.property_id);
    }

    // 3. Send validation emails to buyer and seller
    sendNotaireDossierValidatedEmail({
      buyer_name: dossier?.buyer?.full_name,
      buyer_email: dossier?.buyer?.email,
      seller_name: dossier?.seller?.full_name,
      seller_email: dossier?.seller?.email,
      notaire_name: dossier?.notary?.name,
      dossier_reference: dossier?.reference,
      property_title: dossier?.property?.title,
      validated_at: dossier?.validated_at || new Date()?.toISOString(),
    })?.catch(err => console.warn('[NotairePortal] Validation email error:', err?.message));

    return result;
  } catch (err) {
    return { data: null, error: 'Impossible de valider la conformité du dossier.' };
  }
};

// ─── Documents ────────────────────────────────────────────────────────────────

export const getDocumentsByDossier = async (dossierId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_documents')
      ?.select(`
        *,
        uploader:user_profiles!notaire_documents_uploaded_by_fkey(id, full_name)
      `)
      ?.eq('dossier_id', dossierId)
      ?.order('created_at', { ascending: false });
    if (error) return { data: [], error: error?.message };
    return { data: data || [], error: null };
  } catch (err) {
    return { data: [], error: 'Impossible de charger les documents.' };
  }
};

export const updateDocumentStatus = async (documentId, status, notaryNotes = '') => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_documents')
      ?.update({ status, notary_notes: notaryNotes, reviewed_at: new Date()?.toISOString() })
      ?.eq('id', documentId)
      ?.select(`
        *,
        dossier:notaire_dossiers(
          id,
          reference,
          property:properties(title),
          buyer:user_profiles!notaire_dossiers_buyer_id_fkey(full_name, email),
          seller:user_profiles!notaire_dossiers_seller_id_fkey(full_name, email),
          notary:notaries(name)
        )
      `)
      ?.single();
    if (error) return { data: null, error: error?.message };

    // Send rejection email when a document is rejected
    if (status === 'rejete' && data?.dossier) {
      const d = data?.dossier;
      sendNotaireDossierRejectedEmail({
        buyer_name: d?.buyer?.full_name,
        buyer_email: d?.buyer?.email,
        seller_name: d?.seller?.full_name,
        seller_email: d?.seller?.email,
        notaire_name: d?.notary?.name,
        dossier_reference: d?.reference,
        property_title: d?.property?.title,
        rejection_reason: notaryNotes || null,
      })?.catch(err => console.warn('[NotairePortal] Rejection email error:', err?.message));
    }

    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de mettre à jour le document.' };
  }
};

export const requestComplementaryDocument = async (dossierId, documentName, documentType) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_documents')
      ?.insert([{
        dossier_id: dossierId,
        document_name: documentName,
        document_type: documentType,
        status: 'complementaire_demande',
      }])
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };

    // Fetch dossier parties to send email notification
    const { data: dossier } = await supabase
      ?.from('notaire_dossiers')
      ?.select(`
        reference,
        property:properties(title),
        buyer:user_profiles!notaire_dossiers_buyer_id_fkey(full_name, email),
        seller:user_profiles!notaire_dossiers_seller_id_fkey(full_name, email),
        notary:notaries(name)
      `)
      ?.eq('id', dossierId)
      ?.single();

    if (dossier) {
      sendNotaireMissingDocumentEmail({
        buyer_name: dossier?.buyer?.full_name,
        buyer_email: dossier?.buyer?.email,
        seller_name: dossier?.seller?.full_name,
        seller_email: dossier?.seller?.email,
        notaire_name: dossier?.notary?.name,
        dossier_reference: dossier?.reference,
        property_title: dossier?.property?.title,
        document_name: documentName,
        document_type: documentType,
      })?.catch(err => console.warn('[NotairePortal] Missing document email error:', err?.message));
    }

    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de demander le document.' };
  }
};

// ─── Document Upload ──────────────────────────────────────────────────────────

export const uploadVerificationDocument = async (dossierId, file, documentName, documentType, uploadedByRole = 'partie') => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return { data: null, error: 'Non authentifié.' };

    // Upload file to storage
    const fileExt = file?.name?.split('.')?.pop();
    const filePath = `${user?.id}/${dossierId}/${documentType}_${Date.now()}.${fileExt}`;

    const { data: storageData, error: storageError } = await supabase?.storage
      ?.from('notaire-documents')
      ?.upload(filePath, file, { upsert: false });

    if (storageError) return { data: null, error: storageError?.message };

    // Get signed URL for reference
    const { data: urlData } = await supabase?.storage
      ?.from('notaire-documents')
      ?.createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 days

    // Insert document record
    const { data, error } = await supabase
      ?.from('notaire_documents')
      ?.insert([{
        dossier_id: dossierId,
        uploaded_by: user?.id,
        document_name: documentName,
        document_type: documentType,
        file_url: urlData?.signedUrl || null,
        file_path: filePath,
        file_size: file?.size,
        mime_type: file?.type,
        uploaded_by_role: uploadedByRole,
        status: 'en_attente',
        compliance_checklist: getDefaultChecklist(documentType),
        compliance_score: 0,
      }])
      ?.select()
      ?.single();

    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de téléverser le document.' };
  }
};

export const getDocumentSignedUrl = async (filePath) => {
  try {
    const { data, error } = await supabase?.storage
      ?.from('notaire-documents')
      ?.createSignedUrl(filePath, 60 * 60); // 1 hour
    if (error) return { url: null, error: error?.message };
    return { url: data?.signedUrl, error: null };
  } catch (err) {
    return { url: null, error: 'Impossible de générer le lien.' };
  }
};

export const updateDocumentCompliance = async (documentId, checklist) => {
  try {
    const completed = Object.values(checklist || {})?.filter(Boolean)?.length;
    const total = Object.keys(checklist || {})?.length;
    const score = total > 0 ? Math.round((completed / total) * 100) : 0;

    const { data, error } = await supabase
      ?.from('notaire_documents')
      ?.update({ compliance_checklist: checklist, compliance_score: score })
      ?.eq('id', documentId)
      ?.select()
      ?.single();
    if (error) return { data: null, error: error?.message };
    return { data, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de mettre à jour la checklist.' };
  }
};

// ─── Compliance Checklists per Document Type ──────────────────────────────────

export const DOCUMENT_COMPLIANCE_CHECKLISTS = {
  titre_propriete: [
    { key: 'original_certified', label: 'Document original ou copie certifiée conforme' },
    { key: 'owner_name_matches', label: 'Nom du propriétaire correspond au dossier' },
    { key: 'property_address_matches', label: 'Adresse du bien correspond' },
    { key: 'no_encumbrances', label: 'Absence de charges ou hypothèques visibles' },
    { key: 'date_valid', label: 'Date d\'établissement lisible et valide' },
    { key: 'notary_stamp', label: 'Cachet ou signature notariale présent' },
  ],
  identite: [
    { key: 'photo_clear', label: 'Photo d\'identité nette et lisible' },
    { key: 'not_expired', label: 'Document non expiré' },
    { key: 'name_matches', label: 'Nom correspond au dossier' },
    { key: 'both_sides', label: 'Recto et verso fournis (si applicable)' },
    { key: 'mrz_readable', label: 'Zone de lecture machine (MRZ) lisible' },
  ],
  plan: [
    { key: 'scale_indicated', label: 'Échelle indiquée sur le plan' },
    { key: 'north_arrow', label: 'Flèche nord présente' },
    { key: 'dimensions_visible', label: 'Dimensions et superficies visibles' },
    { key: 'cadastral_ref', label: 'Référence cadastrale mentionnée' },
    { key: 'surveyor_stamp', label: 'Cachet du géomètre ou de l\'autorité compétente' },
    { key: 'date_recent', label: 'Plan datant de moins de 5 ans' },
  ],
  acte: [
    { key: 'parties_identified', label: 'Toutes les parties clairement identifiées' },
    { key: 'property_described', label: 'Bien immobilier décrit avec précision' },
    { key: 'price_mentioned', label: 'Prix de vente mentionné' },
    { key: 'signatures_present', label: 'Signatures de toutes les parties présentes' },
    { key: 'notary_authenticated', label: 'Authentification notariale présente' },
    { key: 'registration_stamp', label: 'Timbre d\'enregistrement fiscal' },
  ],
  fiscal: [
    { key: 'tax_id_visible', label: 'Numéro d\'identification fiscale visible' },
    { key: 'period_covered', label: 'Période fiscale couverte indiquée' },
    { key: 'amount_clear', label: 'Montants clairement lisibles' },
    { key: 'authority_stamp', label: 'Cachet de l\'administration fiscale' },
    { key: 'no_arrears', label: 'Absence d\'arriérés ou mention explicite' },
  ],
  autre: [
    { key: 'document_legible', label: 'Document lisible et complet' },
    { key: 'date_present', label: 'Date de délivrance présente' },
    { key: 'issuer_identified', label: 'Émetteur du document identifié' },
    { key: 'relevant_to_dossier', label: 'Pertinence par rapport au dossier confirmée' },
  ],
};

export const getDefaultChecklist = (documentType) => {
  const items = DOCUMENT_COMPLIANCE_CHECKLISTS?.[documentType] || DOCUMENT_COMPLIANCE_CHECKLISTS?.autre;
  return items?.reduce((acc, item) => ({ ...acc, [item?.key]: false }), {});
};

// ─── Dashboard Stats ──────────────────────────────────────────────────────────

export const getNotaireDashboardStats = async (notaryId) => {
  try {
    const { data, error } = await supabase
      ?.from('notaire_dossiers')
      ?.select('status, is_verified_badge')
      ?.eq('notary_id', notaryId);
    if (error) return { data: null, error: error?.message };

    const stats = {
      total: data?.length || 0,
      nouveau: data?.filter(d => d?.status === 'nouveau')?.length || 0,
      instruction: data?.filter(d => d?.status === 'instruction_en_cours')?.length || 0,
      valide: data?.filter(d => d?.status === 'dossier_valide')?.length || 0,
      acte: data?.filter(d => d?.status === 'projet_acte_depose')?.length || 0,
      cloture: data?.filter(d => d?.status === 'transaction_cloturee')?.length || 0,
      badges: data?.filter(d => d?.is_verified_badge)?.length || 0,
    };
    return { data: stats, error: null };
  } catch (err) {
    return { data: null, error: 'Impossible de charger les statistiques.' };
  }
};
